import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            hosted_domain?: string;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, string>) => void;
        };
      };
    };
  }
}

type GoogleSignInButtonProps = {
  clientId: string;
  hostedDomain?: string;
  onCredential: (idToken: string) => void;
  disabled?: boolean;
};

const SCRIPT_ID = 'google-gsi-client';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function ensureGoogleScript(onReady: () => void): void {
  if (window.google?.accounts?.id) {
    onReady();
    return;
  }

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    existing.addEventListener('load', onReady, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.addEventListener('load', onReady, { once: true });
  document.head.appendChild(script);
}

export function GoogleSignInButton({
  clientId,
  hostedDomain,
  onCredential,
  disabled,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled || !clientId || !containerRef.current) {
      return undefined;
    }

    function renderButton(): void {
      const container = containerRef.current;
      if (!window.google?.accounts?.id || !container) {
        return;
      }

      container.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredential(response.credential),
        ...(hostedDomain ? { hosted_domain: hostedDomain } : {}),
      });
      window.google.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        width: '360',
        text: 'signin_with',
        locale: 'pt_BR',
      });
    }

    ensureGoogleScript(renderButton);

    const container = containerRef.current;
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [clientId, disabled, hostedDomain, onCredential]);

  return <div ref={containerRef} className="flex min-h-[44px] justify-center" />;
}
